<div class="device-health-page">
    <div class="actions" *ngIf="showBackButton">
        <button type="button" class="btn-action" (click)="backToOrganizations()">
            <fa-icon [icon]="['fas', 'arrow-circle-left']" class="fa-thin"></fa-icon>
            Back
        </button>
    </div>

    <div class="device-health-header">
        <div class="device-health-title-block">
            <h2>Devices</h2>
            <p class="device-health-subtitle" *ngIf="isSuperUser && orgFilterName">
                Organization: {{ orgFilterName }}
            </p>
            <p class="device-health-subtitle" *ngIf="isSuperUser && !orgFilterId">
                All organizations
            </p>
            <p class="last-updated">
                Last updated:
                <span *ngIf="lastUpdatedAt">
                    {{ lastUpdatedLabel }}
                    <span class="last-updated-absolute">({{ lastUpdatedAt | reportingDate }})</span>
                </span>
                <span *ngIf="!lastUpdatedAt">Not yet synchronized</span>
            </p>
        </div>

        <div class="device-health-header-actions">
            <button
                    *ngIf="isSuperUser && orgFilterId"
                    type="button"
                    class="filter-reset-btn"
                    (click)="clearOrgFilter()">
                Show all
            </button>
            <button
                    *ngIf="canRefresh"
                    type="button"
                    class="btn-action"
                    [disabled]="refreshing || loading"
                    (click)="refreshNow()"
                    [attr.aria-busy]="refreshing">
                <fa-icon [icon]="['fas', 'rotate']" [class.spinning]="refreshing"></fa-icon>
                {{ refreshing ? 'Refreshing…' : 'Refresh Now' }}
            </button>
        </div>
    </div>

    <div class="device-health-tabs" role="tablist" aria-label="Device health views" data-testid="device-health-tabs">
        <button type="button"
                role="tab"
                class="device-health-tab"
                data-testid="device-health-tab-current"
                [attr.aria-selected]="activeTab === 'current'"
                [class.active]="activeTab === 'current'"
                (click)="selectMainTab('current')">
            Current Health
        </button>
        <button *ngIf="canViewHistory"
                type="button"
                role="tab"
                class="device-health-tab"
                data-testid="device-health-tab-history"
                [attr.aria-selected]="activeTab === 'history'"
                [class.active]="activeTab === 'history'"
                (click)="selectMainTab('history')">
            Health History
        </button>
    </div>

    <div class="device-health-loading" *ngIf="activeTab === 'current' && loading && !refreshing" role="status">
        Loading device health…
    </div>

    <ng-container *ngIf="activeTab === 'current' && !loading">
        <div
                class="device-health-banner"
                *ngIf="pageStatus === 'unavailable' || pageStatus === 'error'"
                role="alert">
            <p class="banner-title">Device health is temporarily unavailable.</p>
            <p class="banner-detail" *ngIf="lastUpdatedAt">
                Last successful update: {{ lastUpdatedLabel }}
            </p>
            <p class="banner-detail" *ngIf="!lastUpdatedAt && pageStatus === 'unavailable'">
                Device health has not been synchronized yet.
            </p>
            <button
                    *ngIf="canRefresh"
                    type="button"
                    class="btn-action"
                    [disabled]="refreshing"
                    (click)="refreshNow()">
                <fa-icon [icon]="['fas', 'rotate']"></fa-icon>
                {{ refreshing ? 'Refreshing…' : 'Refresh Now' }}
            </button>
        </div>

        <ng-container *ngIf="showSummary">
            <div class="summary-grid" aria-label="Device health summary">
                <section class="summary-card" aria-labelledby="all-devices-heading">
                    <h3 id="all-devices-heading" class="summary-title">
                        All Devices · {{ totalDeviceCount }} total
                    </h3>
                    <div class="summary-rows">
                        <div class="summary-row summary-row--bridge">
                            <span class="summary-category" data-device-category="bridge">Bridge</span>
                            <strong>{{ bridgeCount }}</strong>
                        </div>
                        <div class="summary-row summary-row--camera">
                            <span class="summary-category" data-device-category="camera">Camera</span>
                            <strong>{{ cameraCount }}</strong>
                        </div>
                    </div>
                </section>

                <section class="summary-card" aria-labelledby="device-statuses-heading">
                    <h3 id="device-statuses-heading" class="summary-title">Device Statuses</h3>
                    <div class="summary-rows">
                        <div class="summary-row">
                            <span class="status-pill" data-tone="ok">Online</span>
                            <strong>{{ onlineCount }}</strong>
                        </div>
                        <div class="summary-row">
                            <span class="status-pill" data-tone="bad">Offline</span>
                            <strong>{{ offlineCount }}</strong>
                        </div>
                    </div>
                    <p class="attention-note" *ngIf="needsAttention">
                        Needs attention: {{ attentionCount }} device{{ attentionCount === 1 ? '' : 's' }}.
                    </p>
                    <p class="attention-note ok" *ngIf="!needsAttention && totalDeviceCount > 0">
                        No devices need attention.
                    </p>
                </section>
            </div>

            <div class="device-health-empty" *ngIf="!hasAnyDevices">
                No bridges or cameras to display.
            </div>

            <section class="device-section device-section--bridge" aria-labelledby="bridges-heading"
                     *ngIf="hasAnyDevices || bridgeCount === 0">
                <h3 id="bridges-heading" class="section-heading section-heading--bridge">
                    <fa-icon [icon]="['fas', 'hdd']"></fa-icon>
                    Bridges · {{ bridgeCount }}
                </h3>

                <div class="device-list" *ngIf="mappedBridges.length; else noBridges">
                    <article class="device-card device-card--bridge"
                             data-device-category="bridge"
                             *ngFor="let device of mappedBridges">
                        <div class="device-main">
                            <fa-icon [icon]="['fas', 'hdd']" class="device-icon" aria-hidden="true"></fa-icon>
                            <div class="device-copy">
                                <div class="device-name-row">
                                    <span class="device-category-badge" data-device-category="bridge">Bridge</span>
                                    <div class="device-name">{{ device.name || 'Unnamed bridge' }}</div>
                                </div>
                                <div class="device-org" *ngIf="isSuperUser">
                                    {{ device.organization_name || '—' }}
                                </div>
                            </div>
                        </div>
                        <div class="device-statuses">
                            <div class="status-row">
                                <span class="status-label">Connection</span>
                                <span class="status-pill"
                                      [attr.data-tone]="statusTone(device.connection_status || device.primary_status)">
                                    {{ connectionLabel(device) }}
                                </span>
                            </div>
                            <div class="status-row">
                                <span class="status-label">Cloud</span>
                                <span class="status-pill" [attr.data-tone]="statusTone(device.cloud_connection_status)">
                                    {{ displayStatus(device.cloud_connection_status) }}
                                </span>
                            </div>
                            <div class="status-row">
                                <span class="status-label">Edge</span>
                                <span class="status-pill" [attr.data-tone]="statusTone(device.edge_device_status)">
                                    {{ displayStatus(device.edge_device_status) }}
                                </span>
                            </div>
                        </div>
                    </article>
                </div>
                <ng-template #noBridges>
                    <div class="empty-state" *ngIf="!unassignedBridges.length">No bridges found.</div>
                </ng-template>

                <div class="unassigned-block" *ngIf="isSuperUser && unassignedBridges.length && !orgFilterId">
                    <h4 class="unassigned-heading">Unassigned</h4>
                    <div class="device-list">
                        <article class="device-card device-card--bridge unassigned"
                                 data-device-category="bridge"
                                 *ngFor="let device of unassignedBridges">
                            <div class="device-main">
                                <fa-icon [icon]="['fas', 'hdd']" class="device-icon" aria-hidden="true"></fa-icon>
                                <div class="device-copy">
                                    <div class="device-name-row">
                                        <span class="device-category-badge" data-device-category="bridge">Bridge</span>
                                        <div class="device-name">{{ device.name || 'Unnamed bridge' }}</div>
                                    </div>
                                    <div class="device-org">Unassigned</div>
                                </div>
                            </div>
                            <div class="device-statuses">
                                <div class="status-row">
                                    <span class="status-label">Connection</span>
                                    <span class="status-pill"
                                          [attr.data-tone]="statusTone(device.connection_status || device.primary_status)">
                                        {{ connectionLabel(device) }}
                                    </span>
                                </div>
                                <div class="status-row">
                                    <span class="status-label">Cloud</span>
                                    <span class="status-pill"
                                          [attr.data-tone]="statusTone(device.cloud_connection_status)">
                                        {{ displayStatus(device.cloud_connection_status) }}
                                    </span>
                                </div>
                                <div class="status-row">
                                    <span class="status-label">Edge</span>
                                    <span class="status-pill" [attr.data-tone]="statusTone(device.edge_device_status)">
                                        {{ displayStatus(device.edge_device_status) }}
                                    </span>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <section class="device-section device-section--camera" aria-labelledby="cameras-heading"
                     *ngIf="hasAnyDevices || cameraCount === 0">
                <h3 id="cameras-heading" class="section-heading section-heading--camera">
                    <fa-icon [icon]="['fas', 'video']"></fa-icon>
                    Cameras · {{ cameraCount }}
                </h3>

                <div class="device-list" *ngIf="mappedCameras.length; else noCameras">
                    <article class="device-card device-card--camera"
                             data-device-category="camera"
                             *ngFor="let device of mappedCameras">
                        <div class="device-main">
                            <fa-icon [icon]="['fas', 'video']" class="device-icon" aria-hidden="true"></fa-icon>
                            <div class="device-copy">
                                <div class="device-name-row">
                                    <span class="device-category-badge" data-device-category="camera">Camera</span>
                                    <div class="device-name">{{ device.name || 'Unnamed camera' }}</div>
                                </div>
                                <div class="device-org" *ngIf="isSuperUser">
                                    {{ device.organization_name || '—' }}
                                </div>
                            </div>
                        </div>
                        <div class="device-statuses">
                            <div class="status-row">
                                <span class="status-label">Connection</span>
                                <span class="status-pill"
                                      [attr.data-tone]="statusTone(device.connection_status || device.primary_status)">
                                    {{ connectionLabel(device) }}
                                </span>
                            </div>
                            <div class="status-row">
                                <span class="status-label">Cloud</span>
                                <span class="status-pill" [attr.data-tone]="statusTone(device.cloud_connection_status)">
                                    {{ displayStatus(device.cloud_connection_status) }}
                                </span>
                            </div>
                            <div class="status-row">
                                <span class="status-label">Edge</span>
                                <span class="status-pill" [attr.data-tone]="statusTone(device.edge_device_status)">
                                    {{ displayStatus(device.edge_device_status) }}
                                </span>
                            </div>
                        </div>
                    </article>
                </div>
                <ng-template #noCameras>
                    <div class="empty-state" *ngIf="!unassignedCameras.length">No cameras found.</div>
                </ng-template>

                <div class="unassigned-block" *ngIf="isSuperUser && unassignedCameras.length && !orgFilterId">
                    <h4 class="unassigned-heading">Unassigned</h4>
                    <div class="device-list">
                        <article class="device-card device-card--camera unassigned"
                                 data-device-category="camera"
                                 *ngFor="let device of unassignedCameras">
                            <div class="device-main">
                                <fa-icon [icon]="['fas', 'video']" class="device-icon" aria-hidden="true"></fa-icon>
                                <div class="device-copy">
                                    <div class="device-name-row">
                                        <span class="device-category-badge" data-device-category="camera">Camera</span>
                                        <div class="device-name">{{ device.name || 'Unnamed camera' }}</div>
                                    </div>
                                    <div class="device-org">Unassigned</div>
                                </div>
                            </div>
                            <div class="device-statuses">
                                <div class="status-row">
                                    <span class="status-label">Connection</span>
                                    <span class="status-pill"
                                          [attr.data-tone]="statusTone(device.connection_status || device.primary_status)">
                                        {{ connectionLabel(device) }}
                                    </span>
                                </div>
                                <div class="status-row">
                                    <span class="status-label">Cloud</span>
                                    <span class="status-pill"
                                          [attr.data-tone]="statusTone(device.cloud_connection_status)">
                                        {{ displayStatus(device.cloud_connection_status) }}
                                    </span>
                                </div>
                                <div class="status-row">
                                    <span class="status-label">Edge</span>
                                    <span class="status-pill" [attr.data-tone]="statusTone(device.edge_device_status)">
                                        {{ displayStatus(device.edge_device_status) }}
                                    </span>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </section>
        </ng-container>
    </ng-container>

    <section *ngIf="activeTab === 'history' && canViewHistory"
             class="device-health-history"
             data-testid="device-health-history"
             aria-labelledby="device-health-history-heading">
        <header class="history-header">
            <div>
                <h3 id="device-health-history-heading">Health History</h3>
                <p class="helper-text">
                    Organization-scoped historical analysis for Super Users. Timeline points are
                    observed Device Health samples (Asia/Kolkata). Gaps longer than 30 minutes are
                    shown as missing data — not inferred Online/Offline. Default window is the last
                    7 calendar days (maximum 30). Open incidents can predate the observation floor.
                </p>
            </div>
        </header>

        <div class="history-org-selector" data-testid="history-org-selector">
            <label>
                <span>Organization</span>
                <select [(ngModel)]="historyOrgId"
                        name="historyOrgId"
                        data-testid="history-org-select"
                        (ngModelChange)="onHistoryOrganizationChange(true)">
                    <option [ngValue]="null">Select an organization…</option>
                    <option *ngFor="let org of historyOrgOptions" [ngValue]="org.id">
                        {{ org.name }}
                    </option>
                </select>
            </label>
            <p class="helper-text" *ngIf="historyOrgLoading">Loading organizations…</p>
        </div>

        <div *ngIf="historyOrgId == null" class="empty-state" data-testid="history-org-empty">
            Select an organization to view health history.
        </div>

        <ng-container *ngIf="historyOrgId != null">
            <div class="summary-grid history-summary-grid" aria-label="Organization history summary">
                <section class="summary-card">
                    <h3 class="summary-title">Current Online</h3>
                    <strong class="history-metric" data-testid="history-summary-online">
                        {{ historySnapshotLoading ? '…' : historyOnlineCount }}
                    </strong>
                    <p class="helper-text">From live Device Health for this organization.</p>
                </section>
                <section class="summary-card">
                    <h3 class="summary-title">Current Offline</h3>
                    <strong class="history-metric" data-testid="history-summary-offline">
                        {{ historySnapshotLoading ? '…' : historyOfflineCount }}
                    </strong>
                    <p class="helper-text">Connectivity-down only (not Initializing/Busy/Unknown).</p>
                </section>
                <section class="summary-card">
                    <h3 class="summary-title">Open Incidents</h3>
                    <strong class="history-metric" data-testid="history-summary-open-incidents">
                        {{ openIncidentLoading ? '…' : (openIncidentCount == null ? '—' : openIncidentCount) }}
                    </strong>
                    <p class="helper-text">
                        Open incidents overlapping the selected history date window
                        ({{ historyFromDate || '—' }} → {{ historyToDate || '—' }}, Asia/Kolkata).
                    </p>
                </section>
                <section class="summary-card">
                    <h3 class="summary-title">Selectable devices</h3>
                    <strong class="history-metric" data-testid="history-summary-devices">
                        {{ historySnapshotLoading ? '…' : historyDeviceOptions.length }}
                    </strong>
                    <p class="helper-text">
                        Mapped bridges/cameras with device IDs in the live Device Health snapshot
                        (not a historical count).
                    </p>
                </section>
            </div>

            <div class="history-device-selector" data-testid="history-device-selector">
                <label>
                    <span>Device</span>
                    <select [(ngModel)]="historySelectedDeviceId"
                            name="historySelectedDeviceId"
                            data-testid="history-device-select"
                            (ngModelChange)="onHistoryDeviceChange()">
                        <option [ngValue]="null">Select a device…</option>
                        <option *ngFor="let device of historyDeviceOptions" [ngValue]="device.device_id">
                            {{ device.name || 'Unnamed' }}
                            ({{ device.device_type }})
                        </option>
                    </select>
                </label>
                <p class="helper-text" *ngIf="selectedHistoryDevice">
                    Selected: {{ selectedHistoryDevice.name || 'Unnamed device' }}
                    · {{ selectedHistoryDevice.device_type }}
                    · {{ selectedHistoryDevice.device_id }}
                </p>
                <p class="empty-state" *ngIf="!historySnapshotLoading && historyDeviceOptions.length === 0"
                   data-testid="history-no-devices">
                    No mapped devices with identifiers for this organization.
                </p>
            </div>

            <section class="history-timeline" aria-labelledby="history-timeline-heading"
                     *ngIf="historySelectedDeviceId">
                <h4 id="history-timeline-heading" class="section-heading">Status timeline</h4>
                <div class="timeline-legend" *ngIf="chartLegend.length" data-testid="history-timeline-legend">
                    <span *ngFor="let kind of chartLegend" class="timeline-legend-item" [attr.data-kind]="kind">
                        {{ kind === 'online' ? 'Online'
                            : kind === 'offline' ? 'Offline'
                                : kind === 'gap' ? 'No observation'
                                    : 'Other / degraded' }}
                    </span>
                </div>
                <div *ngIf="chartLoading" class="device-health-loading" role="status">Loading timeline…</div>
                <div *ngIf="!chartLoading && chartError" class="device-health-banner" role="alert">
                    <p class="banner-title">Unable to load timeline.</p>
                    <p class="banner-detail">{{ chartError }}</p>
                </div>
                <div *ngIf="!chartLoading && !chartError && timelineChartData.labels?.length === 0"
                     class="empty-state"
                     data-testid="history-timeline-empty">
                    No observations in this window for the selected device.
                </div>
                <div class="timeline-chart-wrap"
                     *ngIf="!chartLoading && !chartError && (timelineChartData.labels?.length || 0) > 0"
                     data-testid="history-timeline-chart">
                    <canvas baseChart
                            [data]="timelineChartData"
                            [options]="timelineChartOptions"
                            [type]="'line'">
                    </canvas>
                </div>
                <p class="helper-text"
                   *ngIf="chartCoverageNote"
                   data-testid="history-timeline-truncated"
                   role="status">
                    {{ chartCoverageNote }}
                </p>
            </section>

            <div class="history-filters" data-testid="device-health-history-filters" *ngIf="historySelectedDeviceId">
                <label>
                    <span>From</span>
                    <input type="date"
                           [(ngModel)]="historyFromDate"
                           name="historyFromDate"
                           data-testid="history-from-date">
                </label>
                <label>
                    <span>To</span>
                    <input type="date"
                           [(ngModel)]="historyToDate"
                           name="historyToDate"
                           data-testid="history-to-date">
                </label>
                <div class="history-filters__actions">
                    <button type="button"
                            class="btn-action"
                            data-testid="history-apply-filters"
                            (click)="applyHistoryFilters()">
                        Apply
                    </button>
                    <button type="button"
                            class="filter-reset-btn"
                            data-testid="history-reset-filters"
                            (click)="resetHistoryFilters()">
                        Reset to last 7 days
                    </button>
                </div>
            </div>

            <p class="history-validation" *ngIf="historyValidationError" data-testid="history-validation-error"
               role="alert">
                {{ historyValidationError }}
            </p>

            <div *ngIf="historySelectedDeviceId && historyLoading"
                 class="device-health-loading"
                 data-testid="history-loading"
                 role="status">
                Loading device health history…
            </div>

            <div *ngIf="historySelectedDeviceId && !historyLoading && historyError"
                 class="device-health-banner"
                 data-testid="history-error"
                 role="alert">
                <p class="banner-title">Unable to load history.</p>
                <p class="banner-detail">{{ historyError }}</p>
            </div>

            <ng-container
                    *ngIf="historySelectedDeviceId && !historyLoading && !historyValidationError && !historyError">
                <section class="history-block" aria-labelledby="history-incidents-heading">
                    <h4 id="history-incidents-heading" class="section-heading">
                        Incidents · {{ historyIncTotal }}
                    </h4>
                    <div *ngIf="historyIncidents.length === 0"
                         class="empty-state"
                         data-testid="history-incidents-empty">
                        No incidents in this window.
                    </div>
                    <div class="responsive-table-wrapper" *ngIf="historyIncidents.length > 0">
                        <table data-testid="history-incidents-table">
                            <thead>
                            <tr>
                                <th>Device</th>
                                <th>Organization</th>
                                <th>Type</th>
                                <th>State</th>
                                <th>Opened</th>
                                <th>Recovered</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr *ngFor="let row of historyIncidents"
                                [attr.data-state]="row.state"
                                [attr.data-incident-type]="row.incident_type"
                                [class.incident-open]="row.state === 'open'"
                                [class.incident-closed]="row.state === 'closed'">
                                <td>
                                    <div class="history-device-name">{{ row.name || 'Unnamed device' }}</div>
                                    <div class="history-device-meta">
                                        {{ row.device_type }} · {{ row.device_id || row.device_key }}
                                    </div>
                                </td>
                                <td>{{ row.organization_name || '—' }}</td>
                                <td>
                                    <span class="history-pill"
                                          [attr.data-incident-type]="row.incident_type">
                                        {{ incidentTypeLabel(row.incident_type) }}
                                    </span>
                                </td>
                                <td>
                                    <span class="status-pill"
                                          [attr.data-tone]="row.state === 'open' ? 'bad' : 'ok'">
                                        {{ row.state === 'open' ? 'Open' : 'Closed' }}
                                    </span>
                                </td>
                                <td>{{ row.first_offline_at ? (row.first_offline_at | reportingDate) : '—' }}</td>
                                <td>{{ row.recovered_at ? (row.recovered_at | reportingDate) : '—' }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <app-vte-pagination
                            *ngIf="historyIncTotal > 0"
                            [currentPage]="historyIncPage"
                            [pageSize]="historyIncPageSize"
                            [totalRecords]="historyIncTotal"
                            (pageChange)="onHistoryIncPageChange($event)"
                            (pageSizeChange)="onHistoryIncPageSizeChange($event)">
                    </app-vte-pagination>
                </section>

                <section class="history-block" aria-labelledby="history-observations-heading">
                    <h4 id="history-observations-heading" class="section-heading">
                        Observations · {{ historyObsTotal }}
                    </h4>
                    <div *ngIf="historyObservations.length === 0"
                         class="empty-state"
                         data-testid="history-observations-empty">
                        No observations in this window.
                    </div>
                    <div class="responsive-table-wrapper" *ngIf="historyObservations.length > 0">
                        <table data-testid="history-observations-table">
                            <thead>
                            <tr>
                                <th>When</th>
                                <th>Device</th>
                                <th>Organization</th>
                                <th>Kind</th>
                                <th>Connection</th>
                                <th>Cloud</th>
                                <th>Edge</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr *ngFor="let row of historyObservations"
                                [attr.data-observation-kind]="row.observation_kind">
                                <td>{{ row.observed_at ? (row.observed_at | reportingDate) : '—' }}</td>
                                <td>
                                    <div class="history-device-name">{{ row.name || 'Unnamed device' }}</div>
                                    <div class="history-device-meta">
                                        {{ row.device_type }} · {{ row.device_id || row.device_key }}
                                    </div>
                                </td>
                                <td>{{ row.organization_name || '—' }}</td>
                                <td>{{ observationKindLabel(row.observation_kind) }}</td>
                                <td>
                                    <span class="status-pill"
                                          [attr.data-tone]="statusTone(row.connection_status || row.primary_status)">
                                        {{ displayStatus(row.connection_status || row.primary_status) }}
                                    </span>
                                </td>
                                <td>
                                    <span class="status-pill"
                                          [attr.data-tone]="statusTone(row.cloud_connection_status)">
                                        {{ displayStatus(row.cloud_connection_status) }}
                                    </span>
                                </td>
                                <td>
                                    <span class="status-pill" [attr.data-tone]="statusTone(row.edge_device_status)">
                                        {{ displayStatus(row.edge_device_status) }}
                                    </span>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <app-vte-pagination
                            *ngIf="historyObsTotal > 0"
                            [currentPage]="historyObsPage"
                            [pageSize]="historyObsPageSize"
                            [totalRecords]="historyObsTotal"
                            (pageChange)="onHistoryObsPageChange($event)"
                            (pageSizeChange)="onHistoryObsPageSizeChange($event)">
                    </app-vte-pagination>
                </section>
            </ng-container>
        </ng-container>
    </section>
</div>
